import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)

type DateRangePreset =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_30_days"
  | "this_year"
  | "custom"

const DATE_RANGE_PRESETS: DateRangePreset[] = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "this_year",
  "custom"
]

// Keys a JSON locale file can hold — `files.filters.dateRangePresets.<key>`.
const DATE_RANGE_LABEL_KEYS: Record<DateRangePreset, string> = {
  today: "today",
  yesterday: "yesterday",
  last_7_days: "last7Days",
  last_30_days: "last30Days",
  this_year: "thisYear",
  custom: "custom"
}

interface DateRangeFilterState {
  date_range: DateRangePreset | null
  date_after: string | null
  date_before: string | null
}

// Day boundaries are cut in UTC, matching the server's clock.
const matchesDateRange = (
  createdISO: string,
  filters: DateRangeFilterState,
  now: dayjs.Dayjs = dayjs.utc()
): boolean => {
  if (!filters.date_range) return true
  const created = dayjs.utc(createdISO)

  if (filters.date_range === "custom") {
    if (filters.date_after && created.isBefore(dayjs.utc(filters.date_after).startOf("day"))) {
      return false
    }
    if (filters.date_before && created.isAfter(dayjs.utc(filters.date_before).endOf("day"))) {
      return false
    }
    return true
  }

  if (filters.date_range === "today") {
    return !created.isBefore(now.startOf("day"))
  }
  if (filters.date_range === "yesterday") {
    const start = now.subtract(1, "day").startOf("day")
    return !created.isBefore(start) && created.isBefore(now.startOf("day"))
  }
  if (filters.date_range === "last_7_days") {
    return !created.isBefore(now.subtract(7, "day").startOf("day"))
  }
  if (filters.date_range === "last_30_days") {
    return !created.isBefore(now.subtract(30, "day").startOf("day"))
  }
  if (filters.date_range === "this_year") {
    return !created.isBefore(now.startOf("year"))
  }
  return true
}

interface DateBounds {
  after: string | null
  before: string | null
}

// Resolves a preset (or a custom pair) to concrete UTC instants once, so the
// server can filter on them directly instead of every row being re-evaluated
// against "now" in the browser — the same day-boundary rules as
// `matchesDateRange`, restated as a `{after, before}` pair to send.
const resolveDateBounds = (
  filters: DateRangeFilterState,
  now: dayjs.Dayjs = dayjs.utc()
): DateBounds => {
  if (!filters.date_range) return { after: null, before: null }

  if (filters.date_range === "custom") {
    return {
      after: filters.date_after ? dayjs.utc(filters.date_after).startOf("day").toISOString() : null,
      before: filters.date_before ? dayjs.utc(filters.date_before).endOf("day").toISOString() : null
    }
  }

  if (filters.date_range === "today") {
    return { after: now.startOf("day").toISOString(), before: null }
  }
  if (filters.date_range === "yesterday") {
    return {
      after: now.subtract(1, "day").startOf("day").toISOString(),
      before: now.startOf("day").toISOString()
    }
  }
  if (filters.date_range === "last_7_days") {
    return { after: now.subtract(7, "day").startOf("day").toISOString(), before: null }
  }
  if (filters.date_range === "last_30_days") {
    return { after: now.subtract(30, "day").startOf("day").toISOString(), before: null }
  }
  if (filters.date_range === "this_year") {
    return { after: now.startOf("year").toISOString(), before: null }
  }
  return { after: null, before: null }
}

export { DATE_RANGE_LABEL_KEYS, DATE_RANGE_PRESETS, matchesDateRange, resolveDateBounds }
export type { DateBounds, DateRangeFilterState, DateRangePreset }
